import { uploadMultipleFiles } from "apis/documents";
import { Button } from "components/DesignSystem/Button/Button";
import DropDown from "components/DesignSystem/Dropdown/Dropdown";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import {
  NEW_FILE_UPLOAD_FAILED,
  NEW_FILE_UPLOADED,
} from "constants/analyticsEvents";
import {
  ADD_FOLDER_ACTION,
  ADD_PRIVATE_FOLDER_ACTION,
  FILE_UPLOAD_MESSAGE,
  FILE_UPLOADING_MESSAGE,
  UPLOAD_FILE_ACTION,
} from "constants/documents";
import { useAnalytics } from "hooks/useAnalytics";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useEffect } from "react";

type AddActionDropdownProps = {
  groupId: string;
  openAddFolderModal: (value: boolean) => void;
  setLoad: React.Dispatch<React.SetStateAction<boolean>>;
  ParentId: string | null;
  setLoadingFileUploadStart: (value: boolean) => void;
  uploadPayload: Array<File>;
  setUploadPayload: (payload: Array<File>) => void;
  open: () => void;
  isInsidePrivateFolder: boolean;
};

export const AddActionDropdown = ({
  groupId,
  openAddFolderModal,
  setLoad,
  ParentId,
  setLoadingFileUploadStart,
  uploadPayload,
  setUploadPayload,
  open,
  isInsidePrivateFolder,
}: AddActionDropdownProps) => {
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const { trackEvent } = useAnalytics();
  const query = useQuery();
  const searchTerm = query.get("search");
  const { isAdmin } = useRoleBasedView();

  useEffect(() => {
    if (uploadPayload.length > 0) {
      uploadfile();
    }
  }, [uploadPayload]);

  const uploadfile = async () => {
    const loadingtoastId = loadingToast({
      title: "Uploading",
      message: FILE_UPLOADING_MESSAGE,
    });
    try {
      await uploadMultipleFiles({
        groupId,
        uploadPayload,
        parentUuid: ParentId,
      });
      trackEvent(NEW_FILE_UPLOADED, {
        parent_uuid: ParentId || "",
        file: uploadPayload,
      });
      setLoad((prev) => !prev);
      successToast({ message: FILE_UPLOAD_MESSAGE });
    } catch (error: any) {
      trackEvent(NEW_FILE_UPLOAD_FAILED, {
        parent_uuid: ParentId || "",
        file: uploadPayload,
        error: error?.response?.data?.error?.message,
      });
      alertToast({ message: error?.response?.data?.error?.message });
    } finally {
      setUploadPayload([]);
      dismissToast(loadingtoastId);
    }
  };

  const privateFolderCreateOption = isAdmin
    ? [
        {
          title: ADD_PRIVATE_FOLDER_ACTION,
          id: 2,
          action: () => openAddFolderModal(true),
        },
      ]
    : [];

  const folderCreateOption = isInsidePrivateFolder
    ? []
    : [
        {
          title: ADD_FOLDER_ACTION,
          id: 1,
          action: () => openAddFolderModal(false),
        },
      ];

  const addDropdownData = [
    ...folderCreateOption,
    ...privateFolderCreateOption,
    {
      title: UPLOAD_FILE_ACTION,
      id: 3,
      action: () => open(),
    },
  ];

  return (
    <DropDown.Root modal={false}>
      <DropDown.Trigger asChild disabled={!!searchTerm}>
        <div>
          <Button
            size="small"
            customType="primary_outlined"
            disabled={!!searchTerm}
          >
            <span className="t-flex t-items-center t-gap-1.5">
              <SmallPlusIcon /> New
            </span>
          </Button>
        </div>
      </DropDown.Trigger>
      <DropDown.Portal>
        <DropDown.Content sideOffset={6} align="end" className="!t-min-w-40">
          {addDropdownData.map(({ id, action, title }) => (
            <DropDown.Item
              key={id}
              className="hover:t-text-purple data-[disabled]:t-text-neutral-30"
              onSelect={action}
            >
              {title}
            </DropDown.Item>
          ))}
        </DropDown.Content>
      </DropDown.Portal>
    </DropDown.Root>
  );
};
